/* eslint-disable camelcase */
/* eslint-disable class-methods-use-this */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2024 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework from '@greenville/framework';
import { getSnapshot } from 'mobx-state-tree';
import {
  PENDO_PLATFORM,
  AUTH_HOME,
  COURSE_SETTINGS_REQUESTED,
  HTTP_REQUEST_METHODS,
  COURSE_SETTINGS_KEYS,
  THREE_DOT_MENU_CONSTANTS
} from '../constants';
import env from '../env';
import CommonUtils from './CommonUtils';
import { ASSIGNMENT_STATUS_TYPES } from '../../modules/assignableEtext/common/Constants';

/**
 * Pendo utilities
 *
 * @file PendoUtils.js
 * @author PAPPU NIKHIL KUMAR
 */

export default class PendoUtils {
  constructor(context) {
    this.context = context;
  }

  /**
   * Function to Initialize pendo
   * @param {*} user
   *
   */
  static handleInitialization(user, location = AUTH_HOME, customFields = { }) {
    const pendo = window.pendo;
    if (pendo) {
      pendo.initialize({
        visitor: {
          id: user.id,
          platform: PENDO_PLATFORM[location],
          ...customFields
        },
        disablePersistence: true,
        disableCookies: true,
        analytics: {
          excludeEvents: ['load', 'click', 'focus', 'submit', 'change', 'track']
        },
        events: {
          ready: () => {
            pendo.startSendingEvents();
          }
        }
      });
    } else {
      PendoUtils.injectScript().then(() => {
        PendoUtils.handleInitialization(user, location, customFields);
      });
    }
  }

  /**
   * Function to inject Pendo script file
   *
   */
  static injectScript() {
    return new Promise(((resolve) => {
      const script = document.createElement('script');
      script.type = 'text/javascript';

      // Pendo initialization code snippet
      script.innerHTML = `(function (apiKey) {
        (function (p, e, n, d, o) {
          var v, w, x, y, z; o = p[d] = p[d] || {}; o._q = o._q || [];

          v = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track']; for (w = 0, x = v.length; w < x; ++w)(function (m) {

            o[m] = o[m] || function () { o._q[m === v[0] ? 'unshift' : 'push']([m].concat([].slice.call(arguments, 0))); };
          })(v[w]);

          y = e.createElement(n); y.async = !0; y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';

          z = e.getElementsByTagName(n)[0]; z.parentNode.insertBefore(y, z);
        })(window, document, 'script', 'pendo');
      })('${env.PENDO_API_KEY}');`;

      script.async = true;
      script.defer = true;
      document.getElementsByTagName('head')[0].appendChild(script);
      resolve();
    }));
  }

  static updateOptions = (user, location = AUTH_HOME, customFields = { }) => {
    const pendo = window.pendo;
    if (pendo && customFields && !CommonUtils.isEmpty(customFields)) {
      pendo.updateOptions({
        visitor: {
          id: user.id,
          platform: PENDO_PLATFORM[location],
          ...customFields
        }
      });
    }
  };

  /**
   * Get AET assignment counts from the assignment store for Pendo metadata.
   * Published = ASSIGNED, Draft = ASSIGNABLE, Total = Published + Draft.
   *
   * @returns {{ aetTotalAssignmentCount: number, aetPublishedAssignmentCount: number, aetDraftAssignmentCount: number }}
   */
  static getAetAssignmentCounts() {
    const assignmentStore = Framework.getStoreRegistry().getStore('assignment');
    const assignments = assignmentStore?.assignments || [];
    const aetPublishedAssignmentCount = assignments.filter(
      assignment => assignment.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNED
    ).length;
    const aetDraftAssignmentCount = assignments.filter(
      assignment => assignment.assignmentStatus === ASSIGNMENT_STATUS_TYPES.ASSIGNABLE
    ).length;

    return {
      aetTotalAssignmentCount: aetPublishedAssignmentCount + aetDraftAssignmentCount,
      aetPublishedAssignmentCount,
      aetDraftAssignmentCount
    };
  }

  /**
 * Initialize Pendo triggers
 *
 * @returns {void}
 */
  initializePendoTriggers = () => {
    const { flags } = this.context.state;
    const { herobanner } = this.context.props;
    const { isAETEnabled } = getSnapshot(herobanner) || {};
    const { course_id } = this.context.heroDashboardPlugin.getHeroBook() || {};
    const courseMenu = this.context.instructorDashboardUtils.getCourseMenu();
    const hasCourseSettings = courseMenu.some(menu => menu.id === THREE_DOT_MENU_CONSTANTS.COURSE_SETTINGS);

    if (!flags?.enableUserCourseLevelSettings || !course_id || !isAETEnabled || !hasCourseSettings) {
      return;
    }

    herobanner.fetchUserCourseSettings();

    this.context.commonPlugin.subscribe('isReadyForPendoTriggers', () => {
      const heroBook = this.context.heroDashboardPlugin.getHeroBook();
      const { courseSettings } = heroBook || {};
      const isCourseSettingsFetched = (courseSettings && courseSettings.length > 0) || this.isCourseSettingsNotFound();
      const { students, assignment } = this.context.props;
      const studentsList = students.studentsList;
      const { isAssignmentsFetched } = this.context.state;
      const { assignments } = getSnapshot(assignment);
      const isReadyForPendoTriggers = isCourseSettingsFetched
        && studentsList
        && isAssignmentsFetched
        && assignments
        && assignments?.length > 0;

      if (isReadyForPendoTriggers) {
        this.evaluatePendoTriggers();

        return true;
      }

      return false;
    });
  };

  /**
   * Get user course settings keys
   *
   * @returns {Array} User course settings keys
   */
  getUserCourseSettingsKeys() {
    return Object.values(COURSE_SETTINGS_KEYS);
  }

  /**
   * Get default course settings
   *
   * @returns {Array} Default course settings
   */
  getDefaultCourseSettings() {
    return {
      [COURSE_SETTINGS_KEYS.AET_SESSION_COUNT]: null,
      [COURSE_SETTINGS_KEYS.HAS_STUDENTS]: null,
      [COURSE_SETTINGS_KEYS.LAST_NOTIFIED_PAST_DUE_DATE]: null
    };
  }

  /**
   * Get values from course settings
   *
   * @param {Array} courseSettings - Course settings
   * @returns {Object|null} User course settings or null if course settings are not fetched
   */
  getUserCourseSettings() {
    const heroBook = this.context.heroDashboardPlugin.getHeroBook();
    const { courseSettings } = heroBook || {};

    if (!courseSettings || courseSettings.length === 0) {
      return this.getDefaultCourseSettings();
    }

    return {
      ...this.getUserCourseSettingsKeys().reduce((acc, key) => {
        acc[key] = courseSettings.find(setting => setting.key === key)?.value;
        return acc;
      }, {})
    };
  }

  /**
   * Entry: call when AET context is ready (e.g. course loaded, auth home mounted).
   * Collects all trigger updates and makes one API call, then updates Pendo metadata on success.
   */
  evaluatePendoTriggers() {
    const allSettings = [];
    let allPendoMetadata = {};

    const sessionTwoUpdate = this.getSessionTwoTriggerUpdate();
    if (sessionTwoUpdate) {
      allSettings.push(...sessionTwoUpdate.settings);
      allPendoMetadata = { ...allPendoMetadata, ...sessionTwoUpdate.pendoMetadata };
    }

    const newStudentsUpdate = this.getNewStudentsTriggerUpdate();
    if (newStudentsUpdate) {
      allSettings.push(...newStudentsUpdate.settings);
      allPendoMetadata = { ...allPendoMetadata, ...newStudentsUpdate.pendoMetadata };
    }

    const pastDueUpdate = this.getPastDueTriggerUpdate();
    if (pastDueUpdate) {
      allSettings.push(...pastDueUpdate.settings);
      allPendoMetadata = { ...allPendoMetadata, ...pastDueUpdate.pendoMetadata };
    }

    if (allSettings.length > 0) {
      this.patchUserCourseSettings(allSettings, () => {
        if (Object.keys(allPendoMetadata).length > 0) {
          this.context.commonPlugin.InitializePendo(true, allPendoMetadata);
        }
      });
    }
  }

  /**
   * Requirement 1: First 2 AeT Sessions
   * Coachmark: "Manage grade sync and student rosters in Course Settings."
   * Trigger: exactly on 2nd session (isAetSessionTwo: true). Session 1 → false, Session 3+ → false.
   *
   * @returns {{ settings: Array, pendoMetadata: Object }|null}
   */
  getSessionTwoTriggerUpdate() {
    const { aetSessionCount } = this.getUserCourseSettings() || {};
    const {
      sessionId: previousSessionId,
      count: currentAetSessionCount
    } = aetSessionCount || {};

    if (currentAetSessionCount >= 2) {
      return null;
    }

    const currentSessionId = window.piSession.getContextId();

    if (currentSessionId === previousSessionId) {
      return null;
    }

    const newAetSessionCount = (currentAetSessionCount || 0) + 1;
    const pendoMetadata = newAetSessionCount === 2 ? { isAetSessionTwo: true } : {};

    return {
      settings: [{
        key: COURSE_SETTINGS_KEYS.AET_SESSION_COUNT,
        value: { sessionId: currentSessionId, count: newAetSessionCount }
      }],
      pendoMetadata
    };
  }

  /**
   * Requirement 2: 1+ Students Added to Roster
   * Coachmark: "Navigate to Course Settings for roster access."
   * Trigger: once per course when studentsCount > 0 and API does not have hasStudents.
   *
   * @returns {{ settings: Array, pendoMetadata: Object }|null}
   */
  getNewStudentsTriggerUpdate() {
    const { hasStudents } = this.getUserCourseSettings() || {};

    if (hasStudents) {
      return null;
    }

    const { students } = this.context.props;
    const studentsList = students.studentsList;
    const studentsCount = studentsList && studentsList.length;

    if (studentsCount > 0) {
      return {
        settings: [{ key: COURSE_SETTINGS_KEYS.HAS_STUDENTS, value: true }],
        pendoMetadata: { hasAetNewStudents: true }
      };
    }

    return null;
  }

  /**
   * Get the most recent past-due assignment (the one with the latest dueDate in the past).
   *
   * @returns {Object|null} Most recent past-due assignment or null if not found.
   */
  getMostRecentPastDueAssignment() {
    const { assignment } = this.context.props;
    const { assignments } = getSnapshot(assignment);

    if (!assignments || assignments.length === 0) {
      return null;
    }

    const now = new Date();

    // Filter assignments with a dueDate in the past.
    const pastDueAssignments = assignments
      .filter((currentAssignment) => {
        if (!currentAssignment.dueDate) {
          return false;
        }

        // Convert ISO string to Date object
        const assignmentDueDate = new Date(currentAssignment.dueDate);

        return assignmentDueDate < now;
      });

    if (pastDueAssignments.length === 0) {
      return null;
    }

    // Sort by dueDate descending, so [0] is the latest (most recent past-due)
    pastDueAssignments.sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate));

    return pastDueAssignments.at(0);
  }

  /**
   * Requirement 3: Recent Assignment Past Due
   * Coachmark: "Verify the sync status for your most recent assignment."
   * Trigger: once per new past-due date; compare UI's most recent past-due with lastNotifiedPastDueDate.
   *
   * @returns {{ settings: Array, pendoMetadata: Object }|null}
   */
  getPastDueTriggerUpdate() {
    const { lastNotifiedPastDueDate } = this.getUserCourseSettings() || {};

    if (!lastNotifiedPastDueDate) {
      const mostRecentPastDueAssignment = this.getMostRecentPastDueAssignment();

      if (!mostRecentPastDueAssignment) {
        return null;
      }

      return {
        settings: [{
          key: COURSE_SETTINGS_KEYS.LAST_NOTIFIED_PAST_DUE_DATE,
          value: new Date().getTime()
        }],
        pendoMetadata: {
          hasAetNewPastDue: true,
          aetPastDueAssignmentName: mostRecentPastDueAssignment.title || ''
        }
      };
    }

    const mostRecentPastDueAssignment = this.getMostRecentPastDueAssignment();

    if (!mostRecentPastDueAssignment) {
      return null;
    }

    if (new Date(mostRecentPastDueAssignment.dueDate) > new Date(lastNotifiedPastDueDate)) {
      return {
        settings: [{
          key: COURSE_SETTINGS_KEYS.LAST_NOTIFIED_PAST_DUE_DATE,
          value: mostRecentPastDueAssignment.dueDate
        }],
        pendoMetadata: {
          hasAetNewPastDue: true,
          aetPastDueAssignmentName: mostRecentPastDueAssignment.title || ''
        }
      };
    }

    return null;
  }

  /**
   * Check if course settings are not found.
   *
   * @returns {boolean} True if course settings are not found, false otherwise.
   */
  isCourseSettingsNotFound() {
    const { courseSettingsStatus } = this.context.props;
    const { error } = getSnapshot(courseSettingsStatus) || {};

    return error && error?.status === 404;
  }

  /**
   * Persist course settings via a single PATCH/PUT (add/update keys in settings array).
   * Updates Pendo metadata on success via the callback.
   *
   * @param {Array<{ key: string, value: * }>} settings - Array of { key, value } objects
   * @param {Function} callback - Called on API success; use to update Pendo metadata
   */
  patchUserCourseSettings(settings, callback = () => {}) {
    const heroBook = this.context.heroDashboardPlugin.getHeroBook();
    const { course_id } = heroBook || {};
    const isCourseSettingsNotFound = this.isCourseSettingsNotFound();

    Framework.getEventManager().publish(COURSE_SETTINGS_REQUESTED, {
      method: isCourseSettingsNotFound ? HTTP_REQUEST_METHODS.PUT : HTTP_REQUEST_METHODS.PATCH,
      courseId: course_id,
      payload: { settings },
      callback
    });
  }
}
